import React from 'react';
import { Divider, Typography } from '@mui/material';
import Page from 'components/new/Page';
import { PARTNER_TRANSFERS_LIST } from 'adminConstants/routes';
import { Column } from 'further-ui/components/Layout';
import PartnerTransferActionBar from '../PartnerTransferActionBar';
import api from 'lib/trpcClient';
import { useNotification } from 'hooks/ui/useNotification';
import { useFormContext } from 'react-hook-form';
import { PartnerTransfersFormValues } from '../hooks/usePartnerTransfersForm';
import { useLayoutContext } from 'contexts/LayoutContext';
import {
  numberToCurrencyString,
  numberToPercentageString,
} from 'further-ui/utils';
import { useLinkedCapitalActionsTransactions } from './hooks/useLinkedCapitalActionsTransactions';
import { useGeneralCapitalActionsTransactions } from './hooks/useGeneralCapitalActionsTransactions';
import useAdvanceSubscriptionsTransactions from './hooks/useAdvanceSubscriptionsTransactions';
import { useInitializeCapitalActionTransactions } from './hooks/useInitializeCapitalActionTransactions';
import CapitalActionTransactionAllocator from './CapitalActionTransactionAllocator';
import { useFeeTransactions } from './hooks/useFeeTransactions';
import { useInitializeFeeTransactions } from './hooks/useInitializeFeeTransactions';
import FeeTransactionAllocator from './FeeTransactionAllocator';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import PageLoader from 'components/PageLoader';
import ErrorBoundary from 'components/ErrorBoundary';
import FormField from 'further-ui/components/FormField';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import Table from 'further-ui/components/Table';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import { sumBy } from 'lodash';

type Props = {
  onPrevious?: () => void;
  onApprove?: () => void;
  stepper: React.ReactNode;
};

const Review: React.FC<Props> = ({ onPrevious, onApprove, stepper }) => {
  const formMethods = useFormContext<PartnerTransfersFormValues>();
  const { selectedVehicleId } = useLayoutContext();
  const notification = useNotification();
  const [isNavigating, setIsNavigating] = React.useState(false);
  const utils = api.useUtils();

  const partnerTransferId = formMethods.getValues('partnerTransferId');

  const [partnerTransfer] = api.partnerTransfers.getById.useSuspenseQuery({
    partnerTransferId,
    vehicleId: selectedVehicleId ?? '',
  });

  const [vehicle] = api.vehicle.getById.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
  });

  const [linkedCapitalActionsTransactions] =
    api.partnerTransfers.getLinkedCapitalActionsTransactions.useSuspenseQuery({
      partnerTransferId,
      vehicleId: selectedVehicleId ?? '',
    });

  const [generalCapitalActionsTransactions] =
    api.partnerTransfers.getGeneralCapitalActionsTransactions.useSuspenseQuery({
      partnerTransferId,
      vehicleId: selectedVehicleId ?? '',
    });

  const [advanceSubscriptionsTransactions] =
    api.partnerTransfers.getAdvanceSubscriptionsTransactions.useSuspenseQuery({
      partnerTransferId,
      vehicleId: selectedVehicleId ?? '',
    });

  const [feeTransactions] =
    api.partnerTransfers.getFeeTransactions.useSuspenseQuery({
      partnerTransferId,
      vehicleId: selectedVehicleId ?? '',
    });

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const savedTransactions = formMethods.watch('capitalActionTransactions');
  const savedFeeTransactions = formMethods.watch('feeTransactions');

  const linkedExpandedRows = useExpandedRows();
  const generalExpandedRows = useExpandedRows();
  const advanceExpandedRows = useExpandedRows();
  const feeExpandedRows = useExpandedRows();

  const { groupedTransactions: linkedGroupedTransactions } =
    useLinkedCapitalActionsTransactions({
      linkedCapitalActionsTransactions,
      baseCurrency: vehicle.baseCurrency,
      savedTransactions,
    });

  const { groupedTransactions: generalGroupedTransactions } =
    useGeneralCapitalActionsTransactions({
      generalCapitalActionsTransactions,
      baseCurrency: vehicle.baseCurrency,
      savedTransactions,
    });

  const { groupedTransactions: advanceSubscriptionsGroupedTransactions } =
    useAdvanceSubscriptionsTransactions({
      advanceSubscriptionsTransactions,
      baseCurrency: vehicle.baseCurrency,
      savedTransactions,
    });

  const { groupedTransactions: groupedFeeTransactions } = useFeeTransactions({
    feeTransactions,
    baseCurrency: vehicle.baseCurrency,
    savedTransactions: savedFeeTransactions,
  });

  useInitializeCapitalActionTransactions({
    linkedGroupedTransactions,
    generalGroupedTransactions,
    advanceSubscriptionsGroupedTransactions,
    savedTransactions,
    formMethods,
  });

  useInitializeFeeTransactions({
    groupedTransactions: groupedFeeTransactions,
    savedTransactions: savedFeeTransactions,
    formMethods,
  });

  const approvePartnerTransferMutation =
    api.partnerTransfers.approve.useMutation({
      onSuccess: () => {
        notification.success('Partner transfer approved successfully.');
        utils.commitments.list.invalidate();
        utils.lpFee.list.invalidate();
        if (onApprove) {
          onApprove();
        }
      },
      onError: (err) => {
        notification.error(
          err?.data?.cause ?? err?.message ?? 'Something went wrong.',
        );
      },
    });

  const handleApprove = async () => {
    if (!partnerTransferId || !selectedVehicleId) {
      notification.error('Missing required information to approve.');
      return;
    }

    setIsNavigating(true);
    try {
      await approvePartnerTransferMutation.mutateAsync({
        partnerTransferId,
        vehicleId: selectedVehicleId,
        capitalActionTransactions: savedTransactions,
        feeTransactions: savedFeeTransactions,
      });
    } catch {
      // error is handled by the mutation
    } finally {
      setIsNavigating(false);
    }
  };

  return (
    <Page
      goBackAction={PARTNER_TRANSFERS_LIST}
      spacing="lg"
      heading="Partner transfer"
      withHeadingDivider
      customActions={stepper}
      actionBar={
        <PartnerTransferActionBar
          onPrevious={onPrevious}
          onNext={handleApprove}
          nextLabel="Approve transfer"
          isNextLoading={isNavigating}
          isNextDisabled={
            !partnerTransferId ||
            isNavigating ||
            transactionTypesLoading ||
            !transactionTypeOptions.length
          }
          isPreviousDisabled={isNavigating}
        />
      }
    >
      {transactionTypesLoading ? (
        <PageLoader />
      ) : !transactionTypeOptions.length ? (
        <Typography>
          To set up account mappings, please first create some transaction
          types.
        </Typography>
      ) : (
        <>
          <Column spacing="lg">
            <Typography variant="h5">Original commitment</Typography>
            <FormField labelColorLight label="LP">
              <Typography variant="body2" color="grey.900">
                {partnerTransfer.originalCommitment.lpName}
              </Typography>
            </FormField>
            <FormField labelColorLight label="Commitment amount">
              <Typography variant="body2" color="grey.900">
                {numberToCurrencyString(
                  partnerTransfer.originalCommitment.commitmentAmountLcy,
                  { currency: vehicle.baseCurrency },
                )}
              </Typography>
            </FormField>
            <FormField labelColorLight label="Amount to transfer">
              <Typography variant="body2" color="grey.900">
                {numberToPercentageString(
                  partnerTransfer.originalCommitment.transferPercentage,
                )}
              </Typography>
            </FormField>
          </Column>

          <Divider />
          <Column spacing="lg">
            <Typography variant="h5">New commitments</Typography>
            <FormField
              labelColorLight
              label={`${partnerTransfer.newCommitments.transferorName} (Transferor)`}
            >
              <Typography variant="body2" color="grey.900">
                {numberToCurrencyString(
                  partnerTransfer.newCommitments.transferorAmountLcy,
                  { currency: vehicle.baseCurrency },
                )}
              </Typography>
            </FormField>
            {partnerTransfer.newCommitments.transferees.map(
              (transferee, index) => (
                <FormField
                  key={transferee.investorId}
                  labelColorLight
                  label={`${transferee.investorName} (Transferee ${index + 1})`}
                >
                  <Typography variant="body2" color="grey.900">
                    {numberToCurrencyString(transferee.amountLcy, {
                      currency: vehicle.baseCurrency,
                    })}
                  </Typography>
                </FormField>
              ),
            )}
          </Column>

          {linkedGroupedTransactions.length > 0 && (
            <>
              <Divider />
              <Column spacing="sm">
                <Typography variant="h5">
                  Review linked capital actions transactions
                </Typography>
                <Table
                  columns={[
                    {
                      key: 'actionName',
                      label: 'Name',
                      render: ({ actionName }) => (
                        <ColorIndicatorWithText
                          text={actionName}
                          colorKey={{ actionName }}
                        />
                      ),
                    },
                    {
                      key: 'transactions',
                      label: 'Transactions',
                      render: (row) => row.transactions.length,
                    },
                    {
                      key: 'total',
                      label: 'Total',
                      render: (row) =>
                        numberToCurrencyString(
                          sumBy(row.transactions, 'amountIncVatTcy'),
                          { currency: vehicle.baseCurrency },
                        ),
                    },
                  ]}
                  rows={linkedGroupedTransactions.map((g) => ({
                    ...g,
                    id: g.actionId,
                  }))}
                  isExpandableRow={() => true}
                  isExpandedRow={(row) =>
                    linkedExpandedRows.expandedRows.has(row.actionId)
                  }
                  onRowClick={(row) =>
                    linkedExpandedRows.toggleRowExpansion(row.actionId)
                  }
                  onRowExpand={(row) =>
                    linkedExpandedRows.toggleRowExpansion(row.actionId)
                  }
                  getExpandedContent={(row) => (
                    <ErrorBoundary>
                      <CapitalActionTransactionAllocator
                        actionId={row.actionId}
                        transactions={row.transactions}
                        transactionTypeOptions={transactionTypeOptions}
                        vehicle={vehicle}
                        savedTransactions={savedTransactions}
                      />
                    </ErrorBoundary>
                  )}
                />
              </Column>
            </>
          )}

          {generalGroupedTransactions.length > 0 && (
            <>
              <Divider />
              <Column spacing="sm">
                <Typography variant="h5">
                  Review general capital actions transactions
                </Typography>
                <Table
                  columns={[
                    {
                      key: 'actionName',
                      label: 'Name',
                      render: ({ actionName }) => (
                        <ColorIndicatorWithText
                          text={actionName}
                          colorKey={{ actionName }}
                        />
                      ),
                    },
                    {
                      key: 'transactions',
                      label: 'Transactions',
                      render: (row) => row.transactions.length,
                    },
                    {
                      key: 'total',
                      label: 'Total',
                      render: (row) =>
                        numberToCurrencyString(
                          sumBy(row.transactions, 'amountIncVatTcy'),
                          { currency: vehicle.baseCurrency },
                        ),
                    },
                  ]}
                  rows={generalGroupedTransactions.map((g) => ({
                    ...g,
                    id: g.actionId,
                  }))}
                  isExpandableRow={() => true}
                  isExpandedRow={(row) =>
                    generalExpandedRows.expandedRows.has(row.id)
                  }
                  onRowClick={(row) =>
                    generalExpandedRows.toggleRowExpansion(row.id)
                  }
                  onRowExpand={(row) =>
                    generalExpandedRows.toggleRowExpansion(row.id)
                  }
                  getExpandedContent={(row) => (
                    <ErrorBoundary>
                      <CapitalActionTransactionAllocator
                        actionId={row.actionId}
                        transactions={row.transactions}
                        transactionTypeOptions={transactionTypeOptions}
                        vehicle={vehicle}
                        savedTransactions={savedTransactions}
                      />
                    </ErrorBoundary>
                  )}
                />
              </Column>
            </>
          )}

          {advanceSubscriptionsGroupedTransactions.length > 0 && (
            <>
              <Divider />
              <Column spacing="sm">
                <Typography variant="h5">
                  Review advance subscriptions transactions
                </Typography>
                <Table
                  columns={[
                    {
                      key: 'name',
                      label: 'Name',
                      render: ({ actionName }) => (
                        <ColorIndicatorWithText
                          text={actionName}
                          colorKey={{ actionName }}
                        />
                      ),
                    },
                    {
                      key: 'transactions',
                      label: 'Transactions',
                      render: (row) => row.transactions.length,
                    },
                    {
                      key: 'total',
                      label: 'Total',
                      render: (row) =>
                        numberToCurrencyString(
                          sumBy(row.transactions, 'amountIncVatTcy'),
                          { currency: vehicle.baseCurrency },
                        ),
                    },
                  ]}
                  rows={advanceSubscriptionsGroupedTransactions.map((g) => ({
                    ...g,
                    id: g.actionId,
                  }))}
                  isExpandableRow={() => true}
                  isExpandedRow={(row) =>
                    advanceExpandedRows.expandedRows.has(row.id)
                  }
                  onRowClick={(row) =>
                    advanceExpandedRows.toggleRowExpansion(row.id)
                  }
                  onRowExpand={(row) =>
                    advanceExpandedRows.toggleRowExpansion(row.id)
                  }
                  getExpandedContent={(row) => (
                    <ErrorBoundary>
                      <CapitalActionTransactionAllocator
                        actionId={row.actionId}
                        transactions={row.transactions}
                        transactionTypeOptions={transactionTypeOptions}
                        vehicle={vehicle}
                        savedTransactions={savedTransactions}
                      />
                    </ErrorBoundary>
                  )}
                />
              </Column>
            </>
          )}

          {groupedFeeTransactions.length > 0 && (
            <>
              <Divider />
              <Column spacing="sm">
                <Typography variant="h5">Review fee transactions</Typography>
                <Table
                  columns={[
                    {
                      key: 'feeName',
                      label: 'Name',
                      render: ({ feeName }) => (
                        <ColorIndicatorWithText
                          text={feeName}
                          colorKey={{ feeName }}
                        />
                      ),
                    },
                    {
                      key: 'transactions',
                      label: 'Transactions',
                      render: (row) => row.transactions.length,
                    },
                    {
                      key: 'total',
                      label: 'Total',
                      render: (row) =>
                        numberToCurrencyString(
                          sumBy(row.transactions, 'amountIncVatTcy'),
                          { currency: vehicle.baseCurrency },
                        ),
                    },
                  ]}
                  rows={groupedFeeTransactions.map((g) => ({
                    ...g,
                    id: g.feeId,
                  }))}
                  isExpandableRow={() => true}
                  isExpandedRow={(row) =>
                    feeExpandedRows.expandedRows.has(row.id)
                  }
                  onRowClick={(row) =>
                    feeExpandedRows.toggleRowExpansion(row.id)
                  }
                  onRowExpand={(row) =>
                    feeExpandedRows.toggleRowExpansion(row.id)
                  }
                  getExpandedContent={(row) => (
                    <ErrorBoundary>
                      <FeeTransactionAllocator
                        feeId={row.feeId}
                        transactions={row.transactions}
                        transactionTypeOptions={transactionTypeOptions}
                        vehicle={vehicle}
                        savedTransactions={savedFeeTransactions}
                      />
                    </ErrorBoundary>
                  )}
                />
              </Column>
            </>
          )}
        </>
      )}
    </Page>
  );
};

export default Review;
